import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardActions, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { useServices } from '@/lib/ServicesContext';
import { useAuth } from '@/lib/auth/AuthContext';
import {
  ETYKIETA_STANU, ETYKIETA_ZAKRESU, tresciApi, type Skorka, type WpisHistorii, type WpisHistoriiZastosowania, type ZakresPaska,
  type ZastosowanieSkorki,
} from '@/lib/api/tresci';
import {
  aktywneINadchodzace, aktywneINadchodzaceZastosowania, chwilaWarszawa, oknoWarszawa, opisAdresatow, uzycieSkorki, zbudujBiblioteke,
  zgodnosc, type PozycjaBiblioteki,
} from '@/lib/tresci/biblioteka';
import { DOPISEK_WYNIK_NIEZNANY, komunikatBledu, useDostepZapisuTresci, wynikNieznany } from '@/lib/tresci/tresciUi';
import { etykietaOsoby, useOsobyPelne, type OsobaPelna } from '@/lib/tresci/osobySkorek';
import { PanelZastosowan } from './PanelZastosowan';
import { PodgladAplikacji } from './PodgladAplikacji';
import { WidokUzytkownikaSkorki } from './WidokUzytkownikaSkorki';
import { ZaplanujSkorke } from './ZaplanujSkorke';
import { HarmonogramSkorek, type AkcjeHarmonogramu } from './HarmonogramSkorek';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * BIBLIOTEKA SKÓREK — galeria, podgląd, planowanie GDZIE i KIEDY (OWNER 11.10 → Skórki 2.0 16.10)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Biblioteka = definicja skórki (rewizje systemu głównego). Zastosowanie = gdzie (Wszyscy / Miasto / Szkoła /
 * Konkretne osoby) i kiedy (OD/DO, czas warszawski) — jedna skórka może mieć wiele zastosowań naraz, bez kopii paczki.
 * Najczęstszy scenariusz: kafelek → „Zaplanuj / użyj" → adresaci + termin → „Potwierdź". Bez JSON-a, bez
 * identyfikatorów, bez ponownego wgrywania. Kto co zobaczy, liczy resolver systemu głównego (ten sam co aplikacja) —
 * „Sprawdź widok użytkownika" pokazuje zwycięzcę, powód i przegrane.
 */

const AKCJE_HISTORII: Readonly<Record<string, string>> = {
  CREATE: 'Dodano', UPDATE: 'Zmieniono', SCHEDULE: 'Zaplanowano', ACTIVATE: 'Aktywowano', DISABLE: 'Wyłączono', ROLLBACK: 'Rollback',
  PUBLISH: 'Zaplanowano (opublikowano)',
};

const ETYKIETA_ZASTOSOWANIA: Readonly<Record<ZastosowanieSkorki['effectiveStatus'], string>> = {
  SCHEDULED: 'Zaplanowane', ACTIVE: 'Aktywne', ENDED: 'Zakończone', DISABLED: 'Wyłączone',
};

/** Notatka historii z oknem ISO („okno 2026-10-27T23:00:00Z → …") pokazana w czasie warszawskim. */
function notatkaWarszawa(note: string | null | undefined): string {
  if (!note) return '';
  return note.replace(/\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}:\d{2})/g, (iso) => chwilaWarszawa(iso));
}

function Miniatura({ adres, nazwa, wysokosc = 140 }: { adres: string | null; nazwa: string; wysokosc?: number }) {
  const [blad, setBlad] = useState(false);
  return (
    <Box sx={{ height: wysokosc, display: 'grid', placeItems: 'center', bgcolor: '#1d1b1a', overflow: 'hidden' }}>
      {adres && !blad
        ? <img src={adres} alt={`Miniatura: ${nazwa}`} onError={() => setBlad(true)}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        : <Typography sx={{ color: '#FFD400', fontWeight: 800, fontSize: 28 }} aria-label={`Brak miniatury: ${nazwa}`}>
            {nazwa.slice(0, 2).toUpperCase()}
          </Typography>}
    </Box>
  );
}

const liczbaOdbiorcow = (z: ZastosowanieSkorki) =>
  typeof z.potentialRecipients === 'number' ? `${z.potentialRecipients.toLocaleString('pl-PL')} potencjalnych odbiorców` : null;

export interface BibliotekaSkorekProps {
  /** Zmiana z zewnątrz (dodanie skórki, akcja w szczegółach technicznych) — biblioteka czyta się ponownie. */
  odswiez: number;
  /** Zmiana stąd (zaplanowanie, wyłączenie) — rodzic odświeża pozostałe widoki. */
  onZmiana?: () => void;
}

/** Co pokazać w podglądzie: rewizja (z kafelka albo z zastosowania). */
interface CelPodgladu { revisionId: number; nazwa: string; minSilnik: number }

/** Adresaci zastosowania; osoby (USER) z pełnymi danymi, gdy są dostępne. */
function adresaci(z: ZastosowanieSkorki, osoby: Map<string, OsobaPelna>, maks = 3): string {
  if (z.scope !== 'USER') return opisAdresatow(z, maks);
  const n = z.targets.map((t) => etykietaOsoby(t.id, t.name, osoby));
  return n.length <= maks ? n.join(', ') : `${n.slice(0, maks).join(', ')} +${n.length - maks}`;
}

function WierszZastosowania({ z, osoby, mozePublikowac, zapisDostepny, onPodglad, onWylacz, onHistoria }: {
  z: ZastosowanieSkorki; osoby: Map<string, OsobaPelna>; mozePublikowac: boolean; zapisDostepny: boolean;
  onPodglad: () => void; onWylacz: () => void; onHistoria: () => void;
}) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' }, py: 0.5 }} data-testid={`zastosowanie-${z.id}`}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2">
          <b>{z.name ?? 'skórka'}</b> · {ETYKIETA_ZAKRESU[z.scope]}: {adresaci(z, osoby)} · {oknoWarszawa(z.startsAt, z.endsAt)}
          {z.priority ? ` · priorytet ${z.priority}` : ''}
        </Typography>
        {liczbaOdbiorcow(z) ? <Typography variant="caption" sx={{ color: 'text.secondary' }}>{liczbaOdbiorcow(z)}</Typography> : null}
      </Box>
      <Stack direction="row" spacing={0.5}>
        <Button size="small" onClick={onPodglad}>Podgląd</Button>
        {mozePublikowac ? <Button size="small" color="warning" disabled={!zapisDostepny} onClick={onWylacz}>Wyłącz</Button> : null}
        <Button size="small" onClick={onHistoria}>Historia</Button>
      </Stack>
    </Stack>
  );
}

export function BibliotekaSkorek({ odswiez, onZmiana }: BibliotekaSkorekProps) {
  const { api } = useServices();
  const auth = useAuth();
  const mozePublikowac = auth.has('app.content.publish');
  const zapis = useDostepZapisuTresci();
  const [rewizje, setRewizje] = useState<Skorka[] | null>(null);
  const [zastosowania, setZastosowania] = useState<ZastosowanieSkorki[] | null>(null);
  const [bladListy, setBladListy] = useState<string | null>(null);
  const [bladZastosowan, setBladZastosowan] = useState<string | null>(null);
  const [licznik, setLicznik] = useState(0);
  const [komunikat, setKomunikat] = useState<{ ok: boolean; tekst: string } | null>(null);
  const [podglad, setPodglad] = useState<CelPodgladu | null>(null);
  const [plan, setPlan] = useState<PozycjaBiblioteki | null>(null);
  const [historia, setHistoria] = useState<PozycjaBiblioteki | null>(null);
  const [historiaZast, setHistoriaZast] = useState<ZastosowanieSkorki | null>(null);
  const [widok, setWidok] = useState(false);
  const [panel, setPanel] = useState<PozycjaBiblioteki | null>(null);
  const [planZakres, setPlanZakres] = useState<ZakresPaska>('GLOBAL');
  const [wyrozniona, setWyrozniona] = useState<string | null>(null);

  useEffect(() => {
    let aktualne = true;
    tresciApi.skorki(api)
      .then((s) => { if (aktualne) { setRewizje(s); setBladListy(null); } })
      .catch((e: unknown) => { if (aktualne) setBladListy(komunikatBledu(e)); });
    // Zastosowania osobno: ich błąd NIE chowa biblioteki, ale jest widoczny (nie udaje „nic nie zaplanowano").
    tresciApi.zastosowania(api)
      .then((z) => { if (aktualne) { setZastosowania(Array.isArray(z) ? z : []); setBladZastosowan(null); } })
      .catch((e: unknown) => { if (aktualne) setBladZastosowan(komunikatBledu(e)); });
    return () => { aktualne = false; };
  }, [api, odswiez, licznik]);

  const biblioteka = useMemo(() => (rewizje ? zbudujBiblioteke(rewizje) : null), [rewizje]);
  const v1 = useMemo(() => (rewizje ? aktywneINadchodzace(rewizje) : null), [rewizje]);
  const teraz = useMemo(() => (zastosowania ? aktywneINadchodzaceZastosowania(zastosowania) : null), [zastosowania]);
  const osoby = useOsobyPelne((teraz ? [...teraz.aktywne, ...teraz.nadchodzace] : [])
    .filter((z) => z.scope === 'USER').flatMap((z) => z.targets.map((t) => t.id)));
  const zaplanuj = (p: PozycjaBiblioteki, zakres: ZakresPaska = 'GLOBAL') => { setPlanZakres(zakres); setPlan(p); };
  const poZmianie = useCallback((tekst: string) => {
    setKomunikat({ ok: true, tekst });
    setLicznik((x) => x + 1);
    onZmiana?.();
  }, [onZmiana]);

  const blad = (e: unknown) => {
    setKomunikat({ ok: false, tekst: komunikatBledu(e) + (wynikNieznany(e) ? DOPISEK_WYNIK_NIEZNANY : '') });
    if (wynikNieznany(e)) setLicznik((x) => x + 1);
  };

  const wylaczZastosowanie = async (z: ZastosowanieSkorki): Promise<boolean> => {
    try {
      await tresciApi.wylaczZastosowanie(api, z.id);
      poZmianie(`Wyłączono: ${z.name ?? 'skórka'} · ${adresaci(z, osoby)} · ${oknoWarszawa(z.startsAt, z.endsAt)}.`);
      return true;
    } catch (e) { blad(e); return false; }
  };

  /** Publikacja rewizji sprzed Skórek 2.0 (V1, globalna) — wyłączana jak dotąd. */
  const wylaczV1 = async (s: Skorka, opis: string): Promise<boolean> => {
    try {
      await tresciApi.wylaczSkorke(api, s.id);
      poZmianie(`${opis}: ${s.name}.`);
      return true;
    } catch (e) { blad(e); return false; }
  };

  // Harmonogram (OWNER 21.10) prowadzi do tych samych okien co kafelki — bez własnych zapisów.
  const pozycja = (themeId: string) => biblioteka?.pozycje.find((p) => p.themeId === themeId) ?? null;
  const akcjeHarmonogramu: AkcjeHarmonogramu = {
    historiaZastosowania: setHistoriaZast,
    historiaSkorki: (themeId) => { const p = pozycja(themeId); if (p) setHistoria(p); },
    zarzadzaj: (themeId) => { const p = pozycja(themeId); if (p) setPanel(p); },
    zaplanuj: (themeId, zakres) => { const p = pozycja(themeId); if (p) zaplanuj(p, zakres); },
    wylacz: wylaczZastosowanie,
    wylaczV1: (s) => wylaczV1(s, 'Wyłączono'),
    widokUzytkownika: () => setWidok(true),
    pokazWBibliotece: (themeId) => {
      setWyrozniona(themeId);
      document.querySelector(`[data-testid="kafelek-${CSS.escape(themeId)}"]`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    },
    podglad: (revisionId, nazwa, minSilnik) => setPodglad({ revisionId, nazwa, minSilnik }),
    wBibliotece: (themeId) => !!pozycja(themeId),
  };

  const wiersz = (z: ZastosowanieSkorki) => (
    <WierszZastosowania key={z.id} z={z} osoby={osoby} mozePublikowac={mozePublikowac} zapisDostepny={zapis.dostepny}
      onPodglad={() => setPodglad({ revisionId: z.revisionId, nazwa: z.name ?? 'skórka', minSilnik: z.minEngineVersion })}
      onWylacz={() => { void wylaczZastosowanie(z); }} onHistoria={() => setHistoriaZast(z)} />
  );

  return (
    <Stack spacing={2} data-testid="biblioteka-skorek">
      {bladListy ? (
        <Alert severity="error" role="alert">
          Nie udało się odczytać biblioteki skórek: {bladListy}
          <Button size="small" sx={{ ml: 1 }} onClick={() => setLicznik((x) => x + 1)}>Spróbuj ponownie</Button>
        </Alert>
      ) : null}
      {bladZastosowan ? (
        <Alert severity="error" role="alert">Nie udało się odczytać zastosowań skórek (aktywne i zaplanowane): {bladZastosowan}</Alert>
      ) : null}
      {!rewizje && !bladListy ? <Typography variant="body2" role="status">Wczytywanie biblioteki…</Typography> : null}
      {komunikat ? (
        <Alert severity={komunikat.ok ? 'success' : 'error'} role={komunikat.ok ? 'status' : 'alert'} data-testid="wynik-biblioteki"
          onClose={() => setKomunikat(null)}>
          {komunikat.tekst}
        </Alert>
      ) : null}
      {zapis.powod && mozePublikowac ? <Alert severity="warning" role="status">{zapis.powod}</Alert> : null}

      {rewizje || zastosowania ? (
        <HarmonogramSkorek zastosowania={zastosowania ?? []} rewizje={rewizje ?? []} mozePublikowac={mozePublikowac}
          zapisDostepny={zapis.dostepny} akcje={akcjeHarmonogramu} />
      ) : null}

      {teraz || v1 ? (
        <Card variant="outlined" data-testid="aktywne-i-nadchodzace">
          <CardContent>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="overline">Aktywne teraz</Typography>
              <Button size="small" variant="outlined" onClick={() => setWidok(true)}>Sprawdź widok użytkownika</Button>
            </Stack>
            <Box data-testid="aktywna-teraz">
              {v1?.aktywna ? (
                <Typography variant="body2"><b>{v1.aktywna.name}</b> · Wszyscy (publikacja V1) · {oknoWarszawa(v1.aktywna.startsAt, v1.aktywna.endsAt)}</Typography>
              ) : null}
              {teraz?.aktywne.map(wiersz)}
              {!v1?.aktywna && !teraz?.aktywne.length ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Brak — aplikacja pokazuje standardowy wygląd.</Typography>
              ) : null}
            </Box>
            <Typography variant="overline" sx={{ display: 'block', mt: 2 }}>Nadchodzące</Typography>
            <Box data-testid="nadchodzace">
              {v1?.nadchodzace.map((s) => (
                <Typography key={`v1-${s.id}`} variant="body2"><b>{s.name}</b> · Wszyscy (publikacja V1) · {oknoWarszawa(s.startsAt, s.endsAt)}</Typography>
              ))}
              {teraz?.nadchodzace.map(wiersz)}
              {!v1?.nadchodzace.length && !teraz?.nadchodzace.length ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Nic nie jest zaplanowane.</Typography>
              ) : null}
            </Box>
          </CardContent>
        </Card>
      ) : null}

      {biblioteka ? (
        <>
          <Typography variant="h6" component="h2">Biblioteka skórek ({biblioteka.pozycje.length})</Typography>
          {biblioteka.pozycje.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Biblioteka jest pusta — dodaj pierwszą skórkę.</Typography>
          ) : null}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))' }}>
            {biblioteka.pozycje.map((p) => {
              const z = zgodnosc(p.minSilnik);
              const u = uzycieSkorki(p.themeId, zastosowania ?? []);
              const ostatnieV1 = p.ostatnieUzycie;
              return (
                <Card key={p.themeId} variant="outlined" data-testid={`kafelek-${p.themeId}`}
                  sx={wyrozniona === p.themeId ? { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } : undefined}>
                  <Miniatura adres={p.miniatura} nazwa={p.nazwa} />
                  <CardContent sx={{ pb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{p.nazwa}</Typography>
                    <Stack spacing={0.75} sx={{ mt: 0.5, alignItems: 'flex-start' }}>
                      {u.aktywnych ? (
                        <Chip size="small" color="success" clickable onClick={() => setPanel(p)} aria-haspopup="dialog"
                          label={`Aktywna: ${u.aktywnych} ${u.aktywnych === 1 ? 'zastosowanie' : 'zastosowania'}`} />
                      ) : null}
                      {!u.aktywnych && u.zaplanowanych ? (
                        <Chip size="small" color="info" clickable onClick={() => setPanel(p)} aria-haspopup="dialog"
                          label={`Zaplanowane: ${u.zaplanowanych}`} />
                      ) : null}
                      {p.aktywna ? <Chip size="small" color="success" label={`Aktywna teraz (V1) · ${oknoWarszawa(p.aktywna.startsAt, p.aktywna.endsAt, true)}`} /> : null}
                      {!u.aktywnych && !u.zaplanowanych && !p.aktywna ? <Chip size="small" variant="outlined" label="Nieaktywna" /> : null}
                      <Chip size="small" variant="outlined" label={z.etykieta} color={z.ostrzezenie ? 'warning' : 'default'} />
                      {z.ostrzezenie ? <Typography variant="caption" sx={{ color: 'text.secondary' }}>{z.ostrzezenie}</Typography> : null}
                      <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="najblizsze">
                        Najbliższe: {u.najblizsze
                          ? `${oknoWarszawa(u.najblizsze.startsAt, u.najblizsze.endsAt, true)} · ${opisAdresatow(u.najblizsze, 2)}`
                          : p.zaplanowane.length ? `${oknoWarszawa(p.zaplanowane[0]!.startsAt, p.zaplanowane[0]!.endsAt, true)} · Wszyscy` : 'brak planu'}
                      </Typography>
                      {u.ostatnie || ostatnieV1 ? (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Ostatnio: {u.ostatnie
                            ? `${oknoWarszawa(u.ostatnie.startsAt, u.ostatnie.endsAt, true)} · ${opisAdresatow(u.ostatnie, 2)}`
                            : `${oknoWarszawa(ostatnieV1!.startsAt, ostatnieV1!.endsAt, true)}${ostatnieV1!.startsAt ? `.${new Date(ostatnieV1!.startsAt).getFullYear()}` : ''}`}
                        </Typography>
                      ) : null}
                    </Stack>
                  </CardContent>
                  <CardActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    <Button size="small" onClick={() => setPodglad({ revisionId: p.wzor.id, nazwa: p.nazwa, minSilnik: p.minSilnik })}>Podgląd</Button>
                    {mozePublikowac ? (
                      <Button size="small" variant="contained" disabled={!zapis.dostepny || !p.doZastosowania} onClick={() => zaplanuj(p)}>
                        Zaplanuj / użyj
                      </Button>
                    ) : null}
                    <Button size="small" onClick={() => setHistoria(p)}>Historia</Button>
                    {mozePublikowac && p.aktywna ? (
                      <Button size="small" color="warning" disabled={!zapis.dostepny} onClick={() => { void wylaczV1(p.aktywna!, 'Wyłączono'); }}>Wyłącz (V1)</Button>
                    ) : null}
                  </CardActions>
                </Card>
              );
            })}
          </Box>
          {biblioteka.odrzucone.length ? (
            <Alert severity="warning" role="status" data-testid="odrzucone-paczki">
              Paczki z błędami walidacji (nie trafiają do biblioteki jako gotowe do publikacji):{' '}
              {biblioteka.odrzucone.map((s) => `${s.name} — ${s.validationErrors.slice(0, 2).join('; ') || 'błąd paczki'}`).join(' · ')}
            </Alert>
          ) : null}
        </>
      ) : null}

      {podglad ? <PodgladDialog cel={podglad} onZamknij={() => setPodglad(null)} /> : null}
      {plan ? (
        <ZaplanujSkorke p={plan} poczatkowyZakres={planZakres} onZamknij={() => setPlan(null)}
          onZaplanowano={(tekst) => { setPlan(null); poZmianie(tekst); }}
          onWynikNieznany={() => setLicznik((x) => x + 1)} />
      ) : null}
      {historia ? (
        <HistoriaDialog p={historia} zastosowania={(zastosowania ?? []).filter((z) => z.themeId === historia.themeId)}
          onZdarzenia={setHistoriaZast} onZamknij={() => setHistoria(null)} />
      ) : null}
      {historiaZast ? <HistoriaZastosowaniaDialog z={historiaZast} onZamknij={() => setHistoriaZast(null)} /> : null}
      {widok ? <WidokUzytkownikaSkorki onZamknij={() => setWidok(false)} /> : null}
      {panel ? (
        <PanelZastosowan p={panel} zastosowania={zastosowania ?? []} mozePublikowac={mozePublikowac} zapisDostepny={zapis.dostepny}
          onZamknij={() => setPanel(null)}
          onDodaj={(zakres) => { const cel = panel; setPanel(null); zaplanuj(cel, zakres); }}
          onHistoria={setHistoriaZast}
          onZmiana={(tekst) => poZmianie(tekst)}
          onWynikNieznany={() => setLicznik((x) => x + 1)} />
      ) : null}
    </Stack>
  );
}

function PodgladDialog({ cel, onZamknij }: { cel: CelPodgladu; onZamknij: () => void }) {
  const { api } = useServices();
  const [paczka, setPaczka] = useState<unknown>(undefined);
  const [blad, setBlad] = useState<string | null>(null);
  useEffect(() => {
    let aktualne = true;
    tresciApi.skorka(api, cel.revisionId)
      .then((s) => { if (aktualne) setPaczka(s.package ?? null); })
      .catch((e: unknown) => { if (aktualne) setBlad(komunikatBledu(e)); });
    return () => { aktualne = false; };
  }, [api, cel.revisionId]);
  const z = zgodnosc(cel.minSilnik);
  return (
    <Dialog open onClose={onZamknij} maxWidth="md" fullWidth aria-labelledby="podglad-tytul">
      <DialogTitle id="podglad-tytul">Podgląd: {cel.nazwa}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {z.etykieta}{z.ostrzezenie ? ` — ${z.ostrzezenie}` : ''}
        </Typography>
        {blad ? <Alert severity="error" role="alert">Nie udało się odczytać paczki: {blad}</Alert> : null}
        {paczka === undefined && !blad ? <Typography role="status">Wczytywanie paczki…</Typography> : null}
        {paczka !== undefined && !blad ? <PodgladAplikacji paczka={paczka} nazwa={cel.nazwa} /> : null}
      </DialogContent>
      <DialogActions><Button onClick={onZamknij}>Zamknij</Button></DialogActions>
    </Dialog>
  );
}

function HistoriaDialog({ p, zastosowania, onZdarzenia, onZamknij }: {
  p: PozycjaBiblioteki; zastosowania: ZastosowanieSkorki[]; onZdarzenia: (z: ZastosowanieSkorki) => void; onZamknij: () => void;
}) {
  const { api } = useServices();
  const [wpisy, setWpisy] = useState<WpisHistorii[] | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  useEffect(() => {
    let aktualne = true;
    tresciApi.historiaSkorki(api, p.themeId)
      .then((w) => { if (aktualne) setWpisy([...w].sort((a, b) => Date.parse(b.at) - Date.parse(a.at))); })
      .catch((e: unknown) => { if (aktualne) setBlad(komunikatBledu(e)); });
    return () => { aktualne = false; };
  }, [api, p.themeId]);
  const rewizja = (id: number | undefined) => p.rewizje.find((r) => r.id === id)?.revision ?? '?';
  return (
    <Dialog open onClose={onZamknij} maxWidth="lg" fullWidth aria-labelledby="historia-tytul">
      <DialogTitle id="historia-tytul">Historia: {p.nazwa}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>A. Historia skórki (rewizje i zmiany paczki)</Typography>
        <Table size="small" aria-label="Rewizje skórki">
          <TableHead><TableRow><TableCell>Rewizja</TableCell><TableCell>Stan</TableCell><TableCell>Termin V1 (Warszawa)</TableCell><TableCell>Dodał</TableCell></TableRow></TableHead>
          <TableBody>
            {p.rewizje.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.revision}</TableCell>
                <TableCell>{ETYKIETA_STANU[r.effectiveStatus] ?? r.effectiveStatus}</TableCell>
                <TableCell>{r.startsAt || r.endsAt ? oknoWarszawa(r.startsAt, r.endsAt) : '—'}</TableCell>
                <TableCell>{r.createdBy ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {blad ? <Alert severity="error" role="alert" sx={{ mt: 1 }}>Nie udało się odczytać historii: {blad}</Alert> : null}
        {!wpisy && !blad ? <Typography role="status">Wczytywanie historii…</Typography> : null}
        {wpisy ? (
          <Table size="small" aria-label="Historia skórki" sx={{ mt: 1 }}>
            <TableHead><TableRow><TableCell>Kiedy</TableCell><TableCell>Co</TableCell><TableCell>Rewizja</TableCell><TableCell>Kto</TableCell><TableCell>Szczegóły</TableCell></TableRow></TableHead>
            <TableBody>
              {wpisy.map((w) => (
                <TableRow key={w.id}>
                  <TableCell>{chwilaWarszawa(w.at)}</TableCell>
                  <TableCell>{AKCJE_HISTORII[w.action] ?? w.action}</TableCell>
                  <TableCell>{rewizja(w.revisionId)}</TableCell>
                  <TableCell>{w.actor ?? '—'}</TableCell>
                  <TableCell>{notatkaWarszawa(w.note)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}

        <Typography variant="subtitle1" sx={{ mt: 3, mb: 0.5 }}>B. Historia zastosowań (gdzie i kiedy)</Typography>
        {zastosowania.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Ta skórka nie była jeszcze zaplanowana dla odbiorców.</Typography>
        ) : (
          <Table size="small" aria-label="Zastosowania skórki">
            <TableHead><TableRow>
              <TableCell>Gdzie</TableCell><TableCell>Termin (Warszawa)</TableCell><TableCell>Priorytet</TableCell><TableCell>Stan</TableCell>
              <TableCell>Zaplanował</TableCell><TableCell>Wyłączył</TableCell><TableCell />
            </TableRow></TableHead>
            <TableBody>
              {zastosowania.map((z) => (
                <TableRow key={z.id}>
                  <TableCell>{ETYKIETA_ZAKRESU[z.scope]}: {opisAdresatow(z, 4)}</TableCell>
                  <TableCell>{oknoWarszawa(z.startsAt, z.endsAt)}</TableCell>
                  <TableCell>{z.priority}</TableCell>
                  <TableCell>{ETYKIETA_ZASTOSOWANIA[z.effectiveStatus]}</TableCell>
                  <TableCell>{z.createdBy ?? '—'} · {chwilaWarszawa(z.publishedAt)}</TableCell>
                  <TableCell>{z.disabledAt ? `${z.disabledBy ?? '—'} · ${chwilaWarszawa(z.disabledAt)}` : '—'}</TableCell>
                  <TableCell><Button size="small" onClick={() => onZdarzenia(z)}>Zdarzenia</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>Identyfikator skórki: {p.themeId}</Typography>
      </DialogContent>
      <DialogActions><Button onClick={onZamknij}>Zamknij</Button></DialogActions>
    </Dialog>
  );
}

function HistoriaZastosowaniaDialog({ z, onZamknij }: { z: ZastosowanieSkorki; onZamknij: () => void }) {
  const { api } = useServices();
  const [wpisy, setWpisy] = useState<WpisHistoriiZastosowania[] | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  useEffect(() => {
    let aktualne = true;
    tresciApi.historiaZastosowania(api, z.id)
      .then((w) => { if (aktualne) setWpisy(w); })
      .catch((e: unknown) => { if (aktualne) setBlad(komunikatBledu(e)); });
    return () => { aktualne = false; };
  }, [api, z.id]);
  return (
    <Dialog open onClose={onZamknij} maxWidth="md" fullWidth aria-labelledby="historia-zast-tytul">
      <DialogTitle id="historia-zast-tytul">Historia zastosowania: {z.name ?? 'skórka'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {ETYKIETA_ZAKRESU[z.scope]}: {opisAdresatow(z, 6)} · {oknoWarszawa(z.startsAt, z.endsAt)} · priorytet {z.priority} · rewizja {z.revision}
        </Typography>
        {blad ? <Alert severity="error" role="alert">Nie udało się odczytać historii: {blad}</Alert> : null}
        {!wpisy && !blad ? <Typography role="status">Wczytywanie historii…</Typography> : null}
        {wpisy ? (
          <Table size="small" aria-label="Historia zastosowania">
            <TableHead><TableRow><TableCell>Kiedy</TableCell><TableCell>Co</TableCell><TableCell>Kto</TableCell><TableCell>Stan po zmianie</TableCell></TableRow></TableHead>
            <TableBody>
              {wpisy.map((w) => (
                <TableRow key={w.id}>
                  <TableCell>{chwilaWarszawa(w.at)}</TableCell>
                  <TableCell>{AKCJE_HISTORII[w.action] ?? w.action}</TableCell>
                  <TableCell>{w.actor ?? '—'}</TableCell>
                  <TableCell>{String(w.snapshot?.Status ?? w.snapshot?.status ?? '—')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </DialogContent>
      <DialogActions><Button onClick={onZamknij}>Zamknij</Button></DialogActions>
    </Dialog>
  );
}
